import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { SelectField } from "../../Common/FormElements/SelectField";
import { TextareaField } from "../../Common/FormElements/TextareaField";
import { ImageInputField } from "../../Common/FormElements/ImageInputField";
import { TinyMCEEditorField } from "../../Common/FormElements/TinyMCEEditorField";
import { fetchCategoryDropdowns } from "../../../services/categoryService";

import {
  fetchSubCategoryById,
  updateSubCategory,
} from "../../../services/subCategoryService";

const UpdateSubCategory = () => {
  // Helper function to convert text to URL slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
  };
  const { id } = useParams();

  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "sub-category/update/:id";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
  } = useFormValidation();

  // ✅ Create refs for each field
  const categoryIdRef = useRef(null);
  const subCategoryNameRef = useRef(null);
  const displayOrderRef = useRef(null);
  const subCategoryURLRef = useRef(null);
  const shortDescriptionRef = useRef(null);
  const fullDescriptionRef = useRef(null);
  const pageTitleRef = useRef(null);
  const metaDescriptionRef = useRef(null);
  const imageInputRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    Id: id,
    CategoryId: "",
    SubCategoryName: "",
    SubCategoryURL: "",
    PageTitle: "",
    MetaDescription: "",
    Published: false,
    Featured: false,
    DisplayOrder: 1000,
    FullDescription: "",
    ShortDescription: "",
    ImageURLFile: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchSubCategoryById(id);
        if (response && response.isSuccess && response.result) {
          setFormValues({
            Id: response.result.id,
            CategoryId: response.result.categoryId,
            SubCategoryName: response.result.subCategoryName,
            SubCategoryURL: response.result.subCategoryURL,
            PageTitle: response.result.pageTitle || "",
            MetaDescription: response.result.metaDescription || "",
            Published: response.result.published,
            Featured: response.result.featured,
            DisplayOrder: response.result.displayOrder,
            FullDescription: response.result.fullDescription,
            ShortDescription: response.result.shortDescription,
            ImageURLFile: null, // Reset file input
          });
          setContent(response.result.fullDescription);
          // Set image URL if available
          if (response.result.imageURL) {
            setImageUrl(response.result.imageURL);
          } else {
            setImageUrl(allImages.ImagePlaceholder);
          }
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, []);

  const [imageUrl, setImageUrl] = useState(allImages.ImagePlaceholder);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [content, setContent] = useState("");
  const [categories, setCategories] = useState([]);

  // ✅ Register all fields
  useEffect(() => {
    registerField("CategoryId", categoryIdRef, "Category");
    registerField("SubCategoryName", subCategoryNameRef, "Sub Category");
    registerField("DisplayOrder", displayOrderRef, "Display Order");
    registerField("SubCategoryURL", subCategoryURLRef, "Sub Category URL");
    registerField("ShortDescription", shortDescriptionRef, "Short Description");
    registerField("FullDescription", fullDescriptionRef, "Full Description");
    registerField("PageTitle", pageTitleRef, "Page Title");
    registerField("MetaDescription", metaDescriptionRef, "Meta Description");
    registerField("ImageURLFile", imageInputRef, "Thumb Image");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  // ✅ Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);
  // ✅ Fetch categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoryData = await fetchCategoryDropdowns();
        if (categoryData || categoryData.result.length > 0) {
          setCategories(categoryData.result);
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    loadCategories();
  }, []);

  const handleEditorChange = (content) => {
    setContent(content);
    setFormValues((prevData) => ({
      ...prevData,
      FullDescription: content,
    }));
  };

  // ✅ Fix input handler for different input types
  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      // Auto-populate CategoryURL when CategoryName changes
      // Only auto-populate if URL field is empty or was previously auto-generated
      if (id === "SubCategoryName") {
        // Check if URL field is empty or matches the slug of previous category name
        const previousSlug = convertToSlug(prev.SubCategoryName);
        const shouldAutoPopulate =
          !prev.SubCategoryURL || prev.SubCategoryURL === previousSlug;

        if (shouldAutoPopulate) {
          newValues.SubCategoryURL = convertToSlug(inputValue);
        }
      }

      return newValues;
    });
  };

  // ✅ Fix file handling with cleanup
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, ImageURLFile: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      var responseData = await updateSubCategory(formValues);

      // ✅ Reset all fields using refs
      // resetAllFields();

      // ✅ Complete form reset
      // setFormValues({
      //   SubCategoryName: "",
      //   SubCategoryURL: "",
      //   PageTitle: "",
      //   MetaDescription: "",
      //   Published: false,
      //   DisplayOrder: 1000,
      //   FullDescription: "",
      //   ShortDescription: "",
      //   ImageURLFile: null,
      // });

      // Clean up current image URL
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
      // setImageUrl(allImages.ImagePlaceholder);
      // setContent("");
      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Add Sub Category" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-8">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Add Sub Category</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Sub-Category
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <SelectField
                      ref={categoryIdRef}
                      label="Category"
                      id="CategoryId"
                      value={formValues.CategoryId}
                      onChange={handleInputChange}
                      options={categories}
                      optionValueKey="id"
                      optionLabelKey="categoryName"
                      placeholder="Select Category"
                      required={true}
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={subCategoryNameRef}
                      label="Sub Category"
                      id="SubCategoryName"
                      value={formValues.SubCategoryName}
                      placeholder="Enter your Sub Category Name"
                      onChange={handleInputChange}
                      required={true}
                      validationType="alphanumeric"
                      maxLength={300}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <InputField
                      ref={subCategoryURLRef}
                      label="Sub-Category URL"
                      id="SubCategoryURL"
                      value={formValues.SubCategoryURL}
                      placeholder="Auto generated URL"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="url"
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TextareaField
                      ref={shortDescriptionRef}
                      label="Short Description"
                      id="ShortDescription"
                      value={formValues.ShortDescription}
                      onChange={handleInputChange}
                      placeholder="Enter Short Description"
                      required={true}
                      showCharCount={true}
                      maxLength={1500}
                    />
                  </div>
                  <div className="">
                    <TinyMCEEditorField
                      ref={fullDescriptionRef}
                      id="FullDescription"
                      label="Full Description"
                      content={content}
                      handleEditorChange={handleEditorChange}
                      required={true}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <InputField
                    ref={displayOrderRef}
                    label="Display Order"
                    id="DisplayOrder"
                    value={formValues.DisplayOrder}
                    onChange={handleInputChange}
                    placeholder="Enter Display Order"
                    validationType="numberonly"
                    maxLength={5}
                    required={true}
                  />
                </div>
                <div className="col-lg-6 col-md-6 mb-3 mt-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="Featured" className="form-label pe-3">
                      Featured Sub-Category
                    </label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="Featured"
                        name="Featured"
                        checked={formValues.Featured}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="Featured">
                        {formValues.Featured ? "Yes" : "No"}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Thumb Image <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <ImageInputField
                  ref={imageInputRef}
                  id="ImageURLFile"
                  imageUrl={imageUrl}
                  onFileChange={handleFileChange}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">SEO Manager</h5>
              </div>
              <div className="card-body p-4">
                <TextareaField
                  ref={pageTitleRef}
                  label="Page Title"
                  id="PageTitle"
                  value={formValues.PageTitle}
                  onChange={handleInputChange}
                  placeholder="Enter Page Title"
                  showCharCount={true}
                  maxLength={300}
                  suggestedLength={70}
                />
                <TextareaField
                  ref={metaDescriptionRef}
                  label="Meta Description"
                  id="MetaDescription"
                  value={formValues.MetaDescription}
                  onChange={handleInputChange}
                  placeholder="Enter Meta Description"
                  showCharCount={true}
                  maxLength={500}
                  suggestedLength={160}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default UpdateSubCategory;
